import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { KpiMetricCard } from "@/components/symin/KpiMetricCard";
import { IntelligenceScoreGauge } from "@/components/symin/IntelligenceScoreGauge";
import { AlertBadge } from "@/components/symin/AlertBadge";
import { ProjectMiniMap } from "@/components/symin/ProjectMiniMap";
import { InvestorLinkDialog } from "@/components/symin/InvestorLinkDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { SatelliteStreamChip } from "@/components/symin/SatelliteStreamChip";
import {
  Crosshair, Sparkle, Warning, ListChecks, CaretDown, ArrowSquareOut, Database, MapPin, FileArrowDown, EnvelopeSimple, Faders, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const PRIO_COLOR = { "Priority 1": "#C20511", "Priority 2": "#FBBF24", "Priority 3": "#94A3B8" };
const meta = (label, value) => (value ? (
  <div className="flex flex-col">
    <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
    <span className="text-xs font-medium text-foreground mt-0.5">{value}</span>
  </div>
) : null);

function TargetRow({ t, i }) {
  const [open, setOpen] = useState(false);
  const pc = PRIO_COLOR[t.priority] || "#94A3B8";
  return (
    <div className="border-b border-border/60 last:border-0" data-testid={`target-rank-row-${i}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-accent/40 transition-colors text-left">
        <span className="font-mono text-xs text-muted-foreground w-6">{String(i + 1).padStart(2, "0")}</span>
        <span className="font-mono font-semibold text-sm text-foreground w-14">{t.label}</span>
        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-[3px]" style={{ color: pc, background: `${pc}1F`, border: `1px solid ${pc}59` }}>{(t.priority || "-").replace("Priority ", "P")}</span>
        <span className="hidden md:block text-xs text-muted-foreground flex-1 truncate">{t.commodity || ""}{t.deposit_model ? ` · ${t.deposit_model}` : ""} · {t.area}</span>
        <div className="flex items-center gap-2 ml-auto">
          <div className="hidden sm:block w-24 h-1.5 rounded-full bg-border overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${t.prospectivity}%`, background: "#C20511" }} />
          </div>
          <span className="font-mono font-semibold text-sm w-10 text-right" style={{ color: "#C20511" }}>{t.prospectivity}</span>
          <CaretDown size={14} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 bg-accent/20" data-testid={`target-why-${i}`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">Why this target?</div>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            {[["Prospectivity", t.prospectivity, "#C20511"], ["Structural", t.structural, "#38BDF8"], ["Spectral / Alteration", t.spectral, "#A855F7"], ["Confidence", t.confidence, "#10B981"]].map(([lab, val, col]) => (
              <div key={lab}>
                <div className="flex justify-between text-[11px] mb-1"><span className="text-muted-foreground">{lab}</span><span className="font-mono" style={{ color: col }}>{val}%</span></div>
                <div className="h-1.5 rounded-full bg-border overflow-hidden"><div className="h-full rounded-full" style={{ width: `${val}%`, background: col }} /></div>
              </div>
            ))}
          </div>
          {t.rationale && <p className="text-xs text-muted-foreground leading-relaxed">{t.rationale}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2">
            <AlertBadge level="gold">Conceptual · Field Verification Required</AlertBadge>
            <button onClick={() => toast.info("3D target view - arriving in an upcoming SYMIN release")} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`target-open-3d-${i}`}>
              <ArrowSquareOut size={13} /> Open in 3D
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CommandCentre() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investor, setInvestor] = useState(() => localStorage.getItem("symin-investor-view") === "1");
  useEffect(() => { localStorage.setItem("symin-investor-view", investor ? "1" : "0"); }, [investor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, proj] = await Promise.all([
        api.get(`/projects/${id}/command-summary`),
        api.get(`/projects/${id}`),
      ]);
      setS(sum.data); setProject(proj.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const [briefBusy, setBriefBusy] = useState(false);
  const downloadBrief = async () => {
    setBriefBusy(true);
    try {
      const res = await api.get(`/projects/${id}/command-brief.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(s?.name || project?.name || "project").replace(/\s+/g, "_")}_Command_Brief.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Command Brief downloaded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBriefBusy(false); }
  };

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailNote, setEmailNote] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailTo, setEmailTo] = useState("self");
  const emailBrief = async () => {
    setEmailBusy(true);
    try {
      const res = await api.post(`/projects/${id}/command-brief/email`, { note: emailNote, recipient: emailTo });
      toast.success(`Brief emailed to ${res.data.to}`, { description: emailTo === "client" ? "Sent straight to the client on file." : "Forward it to your client or stakeholder from your inbox." });
      setEmailOpen(false); setEmailNote("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setEmailBusy(false); }
  };

  const k = s?.kpis || {};

  const resetAreaWeight = async (aid) => {
    try {
      await api.delete(`/projects/${id}/report-fusion-weights?area_id=${aid}`);
      toast.success("Per-area override cleared - using the project default.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetAllWeights = async () => {
    try {
      await api.delete(`/projects/${id}/report-fusion-weights?all_areas=true`);
      toast.success("All per-area overrides cleared - every area now uses the project default.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="command" />
      <div className="ml-[64px]">
        {/* Top bar */}
        <header className="h-[52px] sticky top-0 z-[900] bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-4 px-5 overflow-x-auto scrollbar-hide">
          <div className="min-w-0 shrink-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display font-semibold text-base sm:text-lg tracking-tight truncate" data-testid="command-project-name">{s?.name || project?.name || "Project"}</h1>
              {s?.hms
                ? <Badge className="text-[10px] font-bold border-transparent" style={{ background: "#0B1420", color: "#FFFFFF" }}>HMS</Badge>
                : <Badge variant="outline" className="text-[10px]">Standard</Badge>}
            </div>
          </div>
          <SatelliteStreamChip testid="command-stream-chip" />
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center rounded-md border border-border overflow-hidden text-xs" data-testid="view-mode-toggle">
              <button data-testid="view-geologist" onClick={() => setInvestor(false)}
                className={`px-3 h-8 font-medium transition-colors ${!investor ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>Geologist</button>
              <button data-testid="view-investor" onClick={() => setInvestor(true)}
                className={`px-3 h-8 font-medium transition-colors ${investor ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>Summary</button>
            </div>
            <Button size="sm" className="h-8 font-semibold" onClick={downloadBrief} disabled={briefBusy}
              data-testid="command-brief-btn" style={{ background: "#C20511", color: "#FFFFFF" }}>
              <FileArrowDown size={14} className="mr-1.5" /> {briefBusy ? "Preparing…" : "Command Brief"}
            </Button>
            <Button size="sm" variant="outline" className="h-8 px-2.5" onClick={() => setEmailOpen(true)}
              data-testid="command-brief-email-btn" title="Email the Command Brief to your inbox (PDF attached)">
              <EnvelopeSimple size={15} />
            </Button>
            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
              <DialogContent data-testid="email-brief-dialog">
                <DialogHeader>
                  <DialogTitle>Email Command Brief</DialogTitle>
                  <DialogDescription>
                    Send the one-page Command Brief (PDF attached) by email.
                  </DialogDescription>
                </DialogHeader>
                {project?.client_email ? (
                  <div className="grid grid-cols-1 gap-2" data-testid="email-recipient-choice">
                    <button type="button" data-testid="email-to-self" onClick={() => setEmailTo("self")}
                      className={`text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${emailTo === "self" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                      <span className="font-semibold text-foreground">My inbox</span> <span className="text-muted-foreground">· forward it yourself</span>
                    </button>
                    <button type="button" data-testid="email-to-client" onClick={() => setEmailTo("client")}
                      className={`text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${emailTo === "client" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                      <span className="font-semibold text-foreground">Client on file</span> <span className="text-muted-foreground">· {project.client_email}</span>
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="email-no-client">
                    We&rsquo;ll email it to your own inbox. Add a <b>Client Email</b> in Project Setup (Data &amp; Setup) to send it straight to the client.
                  </p>
                )}
                <Textarea data-testid="email-brief-note" value={emailNote} onChange={(e) => setEmailNote(e.target.value)}
                  placeholder="Optional note to include in the email…" className="min-h-[80px]" maxLength={400} />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEmailOpen(false)} data-testid="email-brief-cancel">Cancel</Button>
                  <Button onClick={emailBrief} disabled={emailBusy} data-testid="email-brief-send"
                    className="font-semibold" style={{ background: "#C20511", color: "#FFFFFF" }}>
                    {emailBusy ? "Sending…" : "Send to my inbox"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" className="h-8" onClick={() => nav(`/projects/${id}`)} data-testid="command-open-workspace">
              <Database size={14} className="mr-1.5" /> Data & Setup
            </Button>
            <InvestorLinkDialog pid={id} />
          </div>
        </header>

        {loading ? (
          <div className="p-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-28 rounded-lg bg-card border border-border animate-pulse" />)}
          </div>
        ) : !s ? (
          <div className="p-10 text-center text-muted-foreground">Unable to load project intelligence.</div>
        ) : (
          <div className="max-w-[1640px] mx-auto px-5 py-5 space-y-5">
            {/* meta strip */}
            <div className="flex flex-wrap gap-x-8 gap-y-3 px-4 py-3 rounded-lg bg-card border border-border" data-testid="command-meta">
              {meta("Commodity", s.commodity)}
              {meta("Country", s.country)}
              {meta("Licence", s.licence_number)}
              {meta("Licence Area", s.licence_area ? `${s.licence_area.toLocaleString()} km²` : null)}
              {meta("Stage", s.stage)}
              {meta("Project No.", s.project_number)}
              {meta("Areas", `${k.areas_complete}/${k.areas} processed`)}
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              <KpiMetricCard testid="kpi-targets" title="Exploration Targets" value={k.targets} accent="#C20511" sub={`${k.priority2 || 0} P2 · ${k.priority3 || 0} P3`} />
              <KpiMetricCard testid="kpi-high-priority" title="High-Priority (P1)" value={k.high_priority} accent="#E67332" sub="Priority-1 targets" />
              <KpiMetricCard testid="kpi-prospectivity" title="Max Prospectivity" value={k.max_prospectivity} unit="%" accent="#C20511" sub="Highest ranked target" />
              <KpiMetricCard testid="kpi-confidence" title="Data Confidence" value={k.data_confidence} unit="%" accent="#10B981" sub={`Avg imagery coverage ${k.avg_coverage || 0}%`} />
              <KpiMetricCard testid="kpi-datasets" title="Active Datasets" value={k.active_datasets} accent="#00B4D8" sub={(s.datasets || []).slice(0, 2).join(" · ")} />
            </div>

            {/* gauge + map */}
            <div className="grid lg:grid-cols-3 gap-5">
              <IntelligenceScoreGauge testid="intelligence-gauge" score={k.intelligence} />
              <div className="lg:col-span-2 rounded-lg bg-card border border-border overflow-hidden flex flex-col" data-testid="command-map-card">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5"><MapPin size={13} /> Project Map · Licence Areas</span>
                  <button onClick={() => toast.info("Full GIS Map Suite - arriving in an upcoming SYMIN release")} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowSquareOut size={13} /> Open GIS Suite</button>
                </div>
                <ProjectMiniMap project={project} className="h-[300px] w-full" />
              </div>
            </div>

            {s?.exploration_summary?.text && (
              <div className="rounded-lg bg-card border border-border p-5" data-testid="command-summary-card">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-4 w-4 rounded-[4px]" style={{ background: "#A855F7" }} />
                  <h3 className="font-display font-semibold text-sm">AI Exploration Summary</h3>
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Included in Master PDF</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap max-h-52 overflow-y-auto" data-testid="command-summary-text">{s.exploration_summary.text}</p>
              </div>
            )}
            {/* ranking + right column */}
            <div className="grid lg:grid-cols-3 gap-5">
              {/* Target ranking */}
              <div className={`${investor ? "lg:col-span-3" : "lg:col-span-2"} rounded-lg bg-card border border-border overflow-hidden`} data-testid="target-ranking-card">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="font-display font-semibold text-sm flex items-center gap-2"><Crosshair size={16} style={{ color: "#C20511" }} /> Target Ranking</span>
                  <span className="text-[11px] text-muted-foreground">{(s.top_targets || []).length} of {k.targets}</span>
                </div>
                {(s.top_targets || []).length === 0
                  ? <div className="px-4 py-10 text-center text-sm text-muted-foreground">No targets generated yet - process the licence areas to generate exploration targets.</div>
                  : (s.top_targets || []).map((t, i) => <TargetRow key={t.id || i} t={t} i={i} />)}
              </div>

              {/* right column */}
              {!investor && (
              <div className="space-y-5">
                {/* AI interpretations */}
                <div className="rounded-lg bg-card border border-border overflow-hidden" data-testid="ai-interpretations-card">
                  <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <Sparkle size={16} style={{ color: "#A855F7" }} />
                    <span className="font-display font-semibold text-sm">Latest AI Interpretations</span>
                  </div>
                  <div className="divide-y divide-border/60">
                    {(s.interpretations || []).length === 0
                      ? <div className="px-4 py-6 text-xs text-muted-foreground">Interpretations appear once targets are generated.</div>
                      : (s.interpretations || []).map((it, i) => (
                        <div key={i} className="px-4 py-3" data-testid={`ai-interp-${i}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-semibold text-xs text-foreground">{it.target}</span>
                            <AlertBadge level="purple">AI Hypothesis</AlertBadge>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">{it.text}</p>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Critical alerts */}
                <div className="rounded-lg bg-card border border-border overflow-hidden" data-testid="alerts-card">
                  <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <Warning size={16} style={{ color: "#F43F5E" }} />
                    <span className="font-display font-semibold text-sm">Technical Alerts</span>
                  </div>
                  <div className="p-4 flex flex-col gap-2.5">
                    {(s.alerts || []).length === 0
                      ? <span className="text-xs text-muted-foreground">No active alerts.</span>
                      : (s.alerts || []).map((a, i) => (
                        <div key={i} className="flex items-start gap-2" data-testid={`alert-${i}`}>
                          <AlertBadge level={a.level}>{a.code}</AlertBadge>
                          <span className="text-xs text-muted-foreground leading-snug">{a.text}</span>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Recommended actions */}
                <div className="rounded-lg bg-card border border-border overflow-hidden" data-testid="recommendations-card">
                  <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <ListChecks size={16} style={{ color: "#10B981" }} />
                    <span className="font-display font-semibold text-sm">Recommended Next Actions</span>
                  </div>
                  <ol className="p-4 space-y-2.5">
                    {(s.recommendations || []).map((r, i) => (
                      <li key={i} className="flex gap-2.5 text-xs text-muted-foreground leading-snug" data-testid={`rec-${i}`}>
                        <span className="font-mono font-semibold shrink-0" style={{ color: "#10B981" }}>{String(i + 1).padStart(2, "0")}</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
              )}
            </div>

            {!investor && s.fusion_weights && (
              <div className="rounded-lg bg-card border border-border overflow-hidden" data-testid="fusion-weights-card">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <Faders size={16} style={{ color: "#F59E0B" }} />
                  <span className="font-display font-semibold text-sm">Fusion Weighting</span>
                  <div className="ml-auto flex items-center gap-3">
                    <span className="text-[11px] text-muted-foreground hidden lg:inline">baked into the Master PDF</span>
                    {(s.fusion_weights.areas || []).some((a) => a.override) && (
                      <button onClick={resetAllWeights} data-testid="fusion-weight-reset-all"
                        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                        <ArrowCounterClockwise size={13} /> Reset all to default
                      </button>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-slate-100">
                        <th className="text-left font-medium px-4 py-2">Licence area</th>
                        <th className="text-right font-medium px-3 py-2">Spectral</th>
                        <th className="text-right font-medium px-3 py-2">Structural</th>
                        <th className="text-left font-medium px-3 py-2">Source</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-border" data-testid="fusion-weight-default-row">
                        <td className="px-4 py-2 font-semibold">Project default</td>
                        <td className="px-3 py-2 text-right tabular-nums">{s.fusion_weights.default?.spectral != null ? `${s.fusion_weights.default.spectral}%` : "-"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{s.fusion_weights.default?.structural != null ? `${s.fusion_weights.default.structural}%` : "-"}</td>
                        <td className="px-3 py-2 text-muted-foreground text-xs">{s.fusion_weights.default ? "Analyst default" : "Model default"}</td>
                        <td />
                      </tr>
                      {(s.fusion_weights.areas || []).map((a) => {
                        const eff = a.override || s.fusion_weights.default;
                        const isOverride = !!a.override;
                        return (
                          <tr key={a.area_id} className="border-t border-border/60 hover:bg-white/[0.02]" data-testid={`fusion-weight-row-${a.area_id}`}>
                            <td className="px-4 py-2">{a.name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{eff?.spectral != null ? `${eff.spectral}%` : "-"}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{eff?.structural != null ? `${eff.structural}%` : "-"}</td>
                            <td className="px-3 py-2">
                              {isOverride
                                ? <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#16243D] text-[#C20511] border border-[#C20511]/30">Override</span>
                                : <span className="text-[11px] text-muted-foreground">Default</span>}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {isOverride && (
                                <button onClick={() => resetAreaWeight(a.area_id)} data-testid={`fusion-weight-reset-${a.area_id}`}
                                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                                  <ArrowCounterClockwise size={13} /> Reset
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2.5 text-[10px] text-muted-foreground border-t border-border">
                  Tune per-area weighting in the <button onClick={() => nav(`/projects/${id}/fusion`)} className="text-[#C20511] hover:underline" data-testid="fusion-weights-open-fusion">Multi-Sensor Fusion</button> module. Overrides take precedence over the project default when the Master PDF is generated.
                </div>
              </div>
            )}

            <p className="text-[11px] text-muted-foreground text-center pt-2 pb-6">
              SYMIN Exploration Intelligence · All targets are conceptual AI-assisted interpretations and require qualified geologist review and field verification before drilling.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
